import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, today } from "@/lib/api";
import { ReceiptCropDialog } from "@/components/ReceiptCropDialog";

const empty = { description: "", date: today(), amount: "", category: "Other" };
export const CATEGORIES = ["Fuel", "Equipment", "Supplies", "Repairs & Maintenance", "Vehicle", "Insurance", "Marketing", "Office", "Meals", "Other"];

export const ExpenseFormDialog = ({ open, onOpenChange, expense, onSaved }) => {
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (open) { setF(expense ? { description: expense.description, date: expense.date, amount: expense.amount, category: expense.category || "Other" } : { ...empty, date: today() }); setFile(null); setPreview(null); }
  }, [open, expense]);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, amount: Number(f.amount) || 0 };
      let { data } = expense ? await api.put(`/expenses/${expense.id}`, payload) : await api.post("/expenses", payload);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { data = (await api.post(`/expenses/${data.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data; }
        catch (err) { toast.error(`Expense saved, but receipt upload failed: ${errMsg(err)}`); }
      }
      toast.success(expense ? "Expense updated" : "Expense added");
      onSaved(data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="expense-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{expense ? "Edit Expense" : "New Expense"}</DialogTitle>
          <DialogDescription>Snap the receipt and it stays attached to this line.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Description *</Label>
            <Input data-testid="expense-form-description-input" required value={f.description} onChange={set("description")} placeholder="Gas, mower blades, trailer tires..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input data-testid="expense-form-date-input" type="date" required value={f.date} onChange={set("date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount ($) *</Label>
              <Input data-testid="expense-form-amount-input" type="number" step="0.01" min="0" required value={f.amount} onChange={set("amount")} placeholder="0.00" className="font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <select data-testid="expense-form-category-select" value={f.category} onChange={set("category")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Receipt photo {expense?.receipt_path && !file ? <span className="text-muted-foreground font-normal">(already attached — choose a new one to replace)</span> : null}</Label>
            <input ref={inputRef} data-testid="expense-form-receipt-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setRawFile(e.target.files?.[0] || null); e.target.value = ""; }} />
            <ReceiptCropDialog file={rawFile} onDone={(f) => { setFile(f); setRawFile(null); }} onCancel={() => setRawFile(null)} />
            {preview ? (
              <div className="relative rounded-lg overflow-hidden border border-border bg-secondary">
                <img src={preview} alt="Receipt preview" data-testid="expense-form-receipt-preview" className="max-h-56 w-full object-contain" />
                <button type="button" data-testid="expense-form-receipt-clear" onClick={() => setFile(null)} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white" aria-label="Remove photo"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button type="button" data-testid="expense-form-receipt-button" onClick={() => inputRef.current?.click()}
                className="w-full h-24 rounded-lg border-2 border-dashed border-border hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground transition-colors">
                <Camera className="h-6 w-6 text-emerald-600" /> Take photo or choose image<span className="text-xs">You can crop it before saving</span>
              </button>
            )}
          </div>
          <Button data-testid="expense-form-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {busy ? "Saving..." : expense ? "Save Changes" : "Add Expense"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
